import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";

const CURRENCY_SYMBOL = {
  usd: "$", gbp: "£", eur: "€", cad: "$", aud: "$", nzd: "$",
  chf: "CHF ", sek: "kr ", nok: "kr ", dkk: "kr ", jpy: "¥",
  inr: "₹", brl: "R$", zar: "R", mxn: "$",
};

const fmtPrice = (n, currency) => {
  const sym = CURRENCY_SYMBOL[(currency || "usd").toLowerCase()] || "$";
  return `${sym}${Number(n || 0).toFixed(0)}`;
};

export default function PublicLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookForm, setBookForm] = useState({ name: "", email: "", phone: "", preferred_date: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: resp } = await api.get(`/public/landing/${slug}`);
        if (alive) setData(resp);
      } catch (err) {
        if (alive) {
          // 404 is handled inline by the not-found state; only surface toasts for unexpected errors.
          if (err.response?.status !== 404) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load coach");
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!bookForm.name.trim() || !bookForm.email.trim() || !bookForm.preferred_date) {
      toast.error("Name, email and preferred date are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/booking-requests", {
        name: bookForm.name.trim(),
        email: bookForm.email.trim().toLowerCase(),
        phone: bookForm.phone.trim() || null,
        preferred_date: bookForm.preferred_date,
        notes: bookForm.notes.trim() || "",
      });
      setSubmitted(true);
      toast.success("Request sent — your coach will be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <p className="tracker text-zinc-500 text-[11px]">Loading…</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col items-center justify-center gap-4" data-testid="landing-not-found">
        <h1 className="font-display text-4xl">Coach not found</h1>
        <p className="text-zinc-500">This landing page is no longer available.</p>
        <Link to="/" className="tracker text-brand-light text-[11px] underline">Back home</Link>
      </div>
    );
  }

  const accent = data.brand_accent_color || "var(--brand-light)";
  const accentBg = { backgroundColor: accent };
  const accentText = { color: accent };
  const accentBorder = { borderColor: accent };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-landing-page" style={{ "--brand-accent": accent }}>
      {/* Hero */}
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-24 flex flex-col items-start gap-6">
          <div className="flex items-center gap-3" data-testid="landing-brand-header">
            {data.brand_logo_b64 ? (
              <img src={data.brand_logo_b64} alt={data.brand_name} className="h-12 w-12 object-contain" />
            ) : (
              <div className="h-12 w-12 flex items-center justify-center font-display text-2xl" style={accentBg}>
                <span className="text-zinc-950">{(data.brand_name || "T").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <p className="tracker text-zinc-500 text-[10px]">[ COACH OPERATING SYSTEM ]</p>
              <p className="font-display text-2xl tracking-tight" data-testid="landing-brand-name">{data.brand_name}</p>
            </div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight" data-testid="landing-hero-headline">
            Train with <span style={accentText}>{data.coach_name || data.brand_name}</span>.
            <br />Every session, every payment, in one place.
          </h1>
          {data.tagline && (
            <p className="text-zinc-400 text-lg max-w-2xl" data-testid="landing-tagline">{data.tagline}</p>
          )}
          <a
            href="#book"
            className="inline-flex items-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 transition-opacity"
            style={accentBg}
            data-testid="landing-cta-book"
          >
            Request a session <ArrowRight size={18} weight="bold" />
          </a>
        </div>
      </header>

      {/* Packages */}
      {data.packages?.length > 0 && (
        <section className="border-b border-zinc-900" data-testid="landing-packages-section">
          <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
            <div className="mb-10">
              <p className="tracker text-zinc-500 text-[10px] mb-2">[ TRAINING PACKAGES ]</p>
              <h2 className="font-display text-3xl sm:text-4xl">Pick your plan.</h2>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-packages-grid">
              {data.packages.map((pkg) => (
                <article
                  key={pkg.id}
                  className="border border-zinc-800 bg-zinc-950 p-6 flex flex-col gap-4 transition-colors hover:border-zinc-700"
                  data-testid={`landing-package-${pkg.slug || pkg.id}`}
                >
                  <div>
                    <h3 className="font-display text-2xl mb-1">{pkg.name}</h3>
                    {pkg.description && (
                      <p className="text-zinc-400 text-sm">{pkg.description}</p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="font-display text-4xl" style={accentText}>{fmtPrice(pkg.price, pkg.currency)}</span>
                    <span className="text-zinc-500 text-sm mb-1.5">{(pkg.currency || "usd").toUpperCase()}</span>
                  </div>
                  <ul className="space-y-2 text-sm text-zinc-300">
                    <li className="flex items-start gap-2">
                      <Check size={14} weight="bold" className="mt-1 shrink-0" style={accentText} />
                      <span>{pkg.sessions_included} × {pkg.session_type}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check size={14} weight="bold" className="mt-1 shrink-0" style={accentText} />
                      <span>Auto-credit to your training pot</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check size={14} weight="bold" className="mt-1 shrink-0" style={accentText} />
                      <span>WhatsApp + email reminders included</span>
                    </li>
                  </ul>
                  <Link
                    to={`/p/${pkg.slug}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 border px-4 py-2.5 font-display text-xs tracking-wide hover:bg-zinc-900 transition-colors"
                    style={accentBorder}
                    data-testid={`landing-package-cta-${pkg.slug || pkg.id}`}
                  >
                    Buy <ArrowRight size={14} weight="bold" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Group sessions */}
      {data.group_enabled && (
        <section className="border-b border-zinc-900" data-testid="landing-group-section">
          <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <p className="tracker text-zinc-500 text-[10px] mb-2">[ GROUP SESSIONS ]</p>
              <h2 className="font-display text-3xl sm:text-4xl">Join a group class.</h2>
              <p className="text-zinc-400 mt-3">Open to everyone — see the schedule and book a spot in seconds.</p>
            </div>
            <Link
              to={`/g/${data.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 transition-opacity shrink-0"
              style={accentBg}
              data-testid="landing-group-cta"
            >
              View class schedule <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>
      )}

      {/* Booking request */}
      <section id="book" className="border-b border-zinc-900" data-testid="landing-booking-section">
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
          <div className="mb-8">
            <p className="tracker text-zinc-500 text-[10px] mb-2">[ REQUEST A SESSION ]</p>
            <h2 className="font-display text-3xl sm:text-4xl">Tell {data.coach_name?.split(" ")[0] || "your coach"} when works.</h2>
            <p className="text-zinc-400 mt-3">No payment up front — your coach will confirm and set up the schedule.</p>
          </div>
          {submitted ? (
            <div className="border p-6 text-center" style={accentBorder} data-testid="landing-booking-success">
              <p className="font-display text-2xl mb-2" style={accentText}>Request sent</p>
              <p className="text-zinc-400 text-sm">You&apos;ll hear back by email shortly.</p>
            </div>
          ) : (
            <form onSubmit={submitBooking} className="grid gap-4 sm:grid-cols-2" data-testid="landing-booking-form">
              <div className="sm:col-span-1">
                <label className="tracker text-zinc-500 text-[10px] block mb-1.5">Name *</label>
                <input
                  type="text"
                  value={bookForm.name}
                  onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                  data-testid="landing-booking-name"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="tracker text-zinc-500 text-[10px] block mb-1.5">Email *</label>
                <input
                  type="email"
                  value={bookForm.email}
                  onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                  data-testid="landing-booking-email"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="tracker text-zinc-500 text-[10px] block mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={bookForm.phone}
                  onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                  data-testid="landing-booking-phone"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="tracker text-zinc-500 text-[10px] block mb-1.5">Preferred date *</label>
                <input
                  type="datetime-local"
                  value={bookForm.preferred_date}
                  onChange={(e) => setBookForm({ ...bookForm, preferred_date: e.target.value })}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                  data-testid="landing-booking-date"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="tracker text-zinc-500 text-[10px] block mb-1.5">Notes</label>
                <textarea
                  rows={3}
                  value={bookForm.notes}
                  onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })}
                  placeholder="Anything your coach should know? Injuries, goals…"
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600 resize-none"
                  data-testid="landing-booking-notes"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  style={accentBg}
                  data-testid="landing-booking-submit"
                >
                  {submitting ? "Sending…" : <>Send request <ArrowRight size={16} weight="bold" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-8 text-center">
        <p className="tracker text-zinc-600 text-[9px]">
          Powered by <Link to="/" className="text-zinc-400 hover:text-zinc-200">Traina</Link>
        </p>
      </footer>
    </div>
  );
}
